import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AnnotationAuthorAvatars — small attribution column painted on the
 * right edge of a card whenever the card carries any annotation.
 * Indicates *who* added the marks at a glance, without requiring the
 * user to open the detail modal.
 *
 * Self-contained: takes just a `blockId` and subscribes to the
 * `userAnnotationStore` directly, so the component can mount as a
 * sibling of any annotation surface (stream-card host, modal panel)
 * without the caller threading the row list through props. That also
 * lets it sit OUTSIDE the scaled `.annotations-container` (which
 * carries `transform: scale(0.5)` on stream cards), so its CSS pixel
 * sizes — see `annotation-author-avatars__avatar` width/height —
 * render at their natural pixel value rather than getting halved.
 *
 * Rendered in BOTH modes:
 *   - "regular" — the stream-card view uses it as a glanceable
 *     "who's been marking this card" hint.
 *   - "expanded" — the modal panel reuses the same column as the
 *     hover-dim target. Hovering a comment (highlight rect or rail
 *     note) or a drawing publishes the hovered annotation's owner
 *     into the shared `useAnnotationHoverStore`; every avatar
 *     whose owner doesn't match dims out, drawing the eye to whose
 *     mark you're attending to.
 *
 * Includes EVERY annotation type — comments, drawings,
 * text_highlight, stickers, stamps, emoji reactions. Anyone who's
 * touched the block in any way counts as an "author"; the row is
 * an attribution affordance, not a thread surface.
 *
 * Dedupes by `ownerId` so a card with five comments from the same
 * author paints one avatar (not five). Multiple authors paint
 * multiple avatars side-by-side, capped at MAX_AVATARS with an
 * overflow `+N` chip so a busy card doesn't spill the row across
 * the full card width.
 *
 * Stacking-context discipline (matches the rest of the annotations
 * layer): no `z-index`, no `isolation: isolate`, no `filter`. The
 * wrapper is `pointer-events: none` so a click on an avatar still
 * bubbles to the card's `block-expand` handler — these are
 * decorative, not interactive surfaces.
 *
 * `opacity` is intentionally OK on the individual avatar (used
 * for the dim feedback below) — the avatar doesn't sit over any
 * `mix-blend-mode` content, so promoting just one avatar to its
 * own stacking context doesn't break neighboring blends.
 */
import { memo, useMemo } from "react";
import { useAccountIdentity } from "@/hooks/use-account-identity";
import { useAnnotationHoverStore } from "@/data/annotation-hover-store";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { OPTIMISTIC_OWNER_PLACEHOLDER } from "@/data/user-annotation-optimistic-id";
const MAX_AVATARS = 5;
export const AnnotationAuthorAvatars = memo(function AnnotationAuthorAvatars({ blockId, }) {
    const { data: annotations } = userAnnotationStore.useByBlock(blockId);
    // Collect unique owner ids of every annotation on the card. Order
    // is stable: first appearance wins, so the row reads in the order
    // authors first marked the card. Every annotation type counts —
    // someone who only stamped or stickered the card is still an
    // author worth surfacing.
    //
    // Optimistic-owner placeholder rows are filtered out so a row
    // mid-create doesn't render as a `?` chip.
    const ownerIds = useMemo(() => {
        const seen = new Set();
        const ids = [];
        for (const a of annotations) {
            const id = a.ownerId;
            if (!id)
                continue;
            if (id === OPTIMISTIC_OWNER_PLACEHOLDER)
                continue;
            if (seen.has(id))
                continue;
            seen.add(id);
            ids.push(id);
        }
        return ids;
    }, [annotations]);
    if (ownerIds.length === 0)
        return null;
    const visibleIds = ownerIds.slice(0, MAX_AVATARS);
    const overflow = ownerIds.length - visibleIds.length;
    return (_jsxs("div", { className: "annotation-author-avatars", "aria-hidden": true, children: [visibleIds.map((ownerId) => (_jsx(AnnotationAuthorAvatar, { ownerId: ownerId, blockId: blockId }, ownerId))), overflow > 0 ? (_jsxs("span", { className: "annotation-author-avatars__overflow", children: ["+", overflow] })) : null] }));
});
const AnnotationAuthorAvatar = memo(function AnnotationAuthorAvatar({ ownerId, blockId, }) {
    // Same identity contract used by the comment popover / rail —
    // resolves a hardcoded portrait when the team has one for
    // this owner, otherwise falls back to an uppercased initial
    // inside the same warm tan circle the lip uses.
    const identity = useAccountIdentity(ownerId);
    // Subscribe to a per-avatar boolean so the avatar only re-renders
    // when its OWN dim state flips, not on every hover anywhere.
    // Dim when:
    //   1. SOME annotation is currently hovered, AND
    //   2. that annotation lives on THIS block (so a hover on a
    //      neighboring stream card doesn't bleed the dim across
    //      cards), AND
    //   3. that annotation's owner isn't us.
    // The "some annotation hovered" gate keeps the idle state (no
    // hover) at full opacity for everyone — we only dim during
    // active hover-attention.
    const isDimmed = useAnnotationHoverStore((s) => {
        if (s.hoveredOwnerId === undefined)
            return false;
        if (s.hoveredBlockId !== blockId)
            return false;
        return s.hoveredOwnerId !== ownerId;
    });
    const className = [
        "annotation-author-avatars__avatar",
        isDimmed && "annotation-author-avatars__avatar--dimmed",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsx("span", { className: className, children: identity.avatarSrc ? (_jsx("img", { src: identity.avatarSrc, alt: "", className: "annotation-author-avatars__image" })) : (_jsx("span", { className: "annotation-author-avatars__initial", children: identity.initial })) }));
});
